<template>
	<ValidationObserver v-slot="{ handleSubmit }" slim>
		<form class="company_data_step" novalidate @submit.prevent="handleSubmit(emitContinue)">
			<h1 class="step_title">Datos de la empresa</h1>
			<p class="step_subtitle">Cuéntanos sobre tu empresa</p>
			<ValidationProvider
				v-slot="{ errors }"
				name="nombre de la empresa"
				rules="required|min:2|max:149"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_company_name">
						Nombre de la empresa*
					</label>
					<input
						id="register_company_name"
						v-model.trim="form.company_name"
						class="field_input"
						:class="{ 'has-error': errors[0] }"
						type="text"
						placeholder="Ej. CFOremoto"
						autocomplete="organization"
					/>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider
				v-slot="{ errors }"
				name="rut de la empresa"
				rules="required|min:11|max:12|rut"
				mode="eager"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_company_rut">
						RUT de la empresa*
					</label>
					<input
						id="register_company_rut"
						v-model.trim="form.company_rut"
						v-mask="rutMask"
						class="field_input"
						:class="{ 'has-error': errors[0] }"
						type="text"
						placeholder="Ej. 77.987.654-3"
					/>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider v-slot="{ errors }" name="qué vende tu empresa" rules="required" slim>
				<fieldset class="field business_type_field">
					<legend class="field_label">¿Qué vende tu empresa? *</legend>
					<p class="field_help">Marca la opción que corresponda.</p>
					<label
						v-for="option in businessTypeOptions"
						:key="option"
						class="radio_option"
					>
						<input
							v-model="form.business_type"
							class="radio_input"
							type="radio"
							name="register_business_type"
							:value="option"
						/>
						<span class="radio_mark"></span>
						{{ option }}
					</label>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</fieldset>
			</ValidationProvider>
			<fieldset v-if="isProducts" class="field business_type_field">
				<legend class="field_label">
					¿Cómo opera tu empresa con los productos? *
				</legend>
				<p class="field_help">Marca la opción que corresponda.</p>
				<label class="radio_option">
					<input
						v-model="productAnswers.has_production_plant"
						class="radio_input"
						type="radio"
						name="register_product_operation"
						:value="false"
					/>
					<span class="radio_mark"></span>
					Compro y vendo productos
				</label>
				<label class="radio_option">
					<input
						v-model="productAnswers.has_production_plant"
						class="radio_input"
						type="radio"
						name="register_product_operation"
						:value="true"
					/>
					<span class="radio_mark"></span>
					Tengo una planta o un maquilador
				</label>
			</fieldset>
			<button
				class="continue_button"
				type="submit"
				:disabled="!isComplete || loading"
			>
				{{ loading ? "Creando tu cuenta…" : "Continuar" }}
			</button>
		</form>
	</ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";

// Define el tipo de negocio (afecta fórmulas de margen/EBITDA en reportes).
export const BUSINESS_TYPE_OPTIONS = ["Productos", "Servicios"];

export default {
	name: "RegisterCompanyDataStep",
	components: {
		ValidationObserver,
		ValidationProvider,
	},
	props: {
		value: {
			type: Object,
			default: () => ({}),
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		const initBool = (v) => (typeof v === "boolean" ? v : null);
		return {
			form: {
				company_name: this.value.company_name || "",
				company_rut: this.value.company_rut || "",
				business_type: this.value.business_type || "",
			},
			// Solo aplica a "Productos": elección excluyente (radio). null = sin
			// responder. false = compro y vendo (Comercializador), true = planta
			// o maquilador (Productor).
			productAnswers: {
				has_production_plant: initBool(this.value.has_production_plant),
			},
			businessTypeOptions: BUSINESS_TYPE_OPTIONS,
			// Mismo formato que FormRegisterFast; B = dígito verificador (0-9/K).
			rutMask: ["#.###.###-B", "##.###.###-B"],
		};
	},
	computed: {
		isProducts() {
			return this.form.business_type === "Productos";
		},
		isComplete() {
			const baseComplete = Object.values(this.form).every(
				(field) => field !== ""
			);
			if (!baseComplete) return false;
			// Productos exige elegir una opción; Servicios no pregunta nada.
			if (this.isProducts) {
				return this.productAnswers.has_production_plant !== null;
			}
			return true;
		},
	},
	methods: {
		emitContinue() {
			// El RUT viaja como se ve (con puntos); se limpia recién en el submit
			// final del wizard. has_production_plant viaja como booleano
			// (Servicios → false; el backend lo ignora y usa Consultor).
			this.$emit("continue", {
				...this.form,
				has_production_plant:
					this.productAnswers.has_production_plant === true,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.business_type_field {
	border: none;
	margin: 0 0 20px;
	padding: 0;
}

.radio_option {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 12px;
	color: var(--auth-text);
	font-size: 14px;
	cursor: pointer;
	user-select: none;
}

// Radio nativo oculto pero accesible (focus/teclado siguen funcionando);
// el círculo visible es .radio_mark.
.radio_input {
	position: absolute;
	opacity: 0;
	width: 16px;
	height: 16px;
	margin: 0;
	cursor: pointer;
}

.radio_mark {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	border: 1.5px solid var(--auth-control-border);
	border-radius: 50%;
	background: var(--auth-control-bg);
	transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;
}

.radio_input:checked + .radio_mark {
	border-color: var(--auth-accent);
	border-width: 5px;
	background: var(--auth-surface);
}

.radio_input:focus-visible + .radio_mark {
	box-shadow: 0 0 0 3px var(--auth-control-ring);
}
</style>
